import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../../../store/rootReducer';
import Loader from '../../Loader';
import QueryString from 'qs';
import PointInput from './pointInput';
import GemstonesInput from './GemstonesInput';
import useAxios from '../../../hooks/useAxios';
import { getPointInformation, savePointInfomation } from '../../../api/point.api';
import Button from '../../../components/Button';
import { warningVariant } from '../../../lib/barVariant';
import { useSnackbar } from 'notistack';
import useNotification from '../../../hooks/useNotification';
import styled from 'styled-components';

interface IProps {}

const TableEmptyBlock = styled.div`
    font-size: 12px;
    color: #999;
    padding: 50px 0;
    background-color: #fbfbfc;
    text-align: center;
`

const PointTab: FC<IProps> = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const query = QueryString.parse(location.search, {ignoreQueryPrefix: true,});
  const { data: pointResponse, isLoading, error, execute } = useAxios<any>();
  // 불러둔 store의 내용을 하위 component에서 사용
  const { isLoading: isLoading2 } = useSelector((state: RootState) => state.classStudent,);
  const { isLoading: isLoading3 } = useSelector((state: RootState) => state.classTimetable,);
  const [classCode, setClassCode] = useState('');
  const [maxPoints, setMaxPoints] = useState<any>([]);
  const [pointSections, setPointSections] = useState<any>([]);
  const [pointPolicyNo, setPointPolicyNo] = useState<number|null|undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { successNotification } = useNotification();

  useEffect(() => {
    if (query.classCode) {
      const _classCode = String(query.classCode);
      setClassCode(_classCode);
    }
  }, [location]);

  useEffect(() => {
    if (!pointResponse?.data) return;

    setMaxPoints(pointResponse.data.maxPoints);
    setPointSections(pointResponse.data.pointSections);


    if (
      pointResponse.data.maxPoints &&
      pointResponse.data.maxPoints.length > 0
    ) {
      const { pointPolicyNo } = pointResponse.data.maxPoints.at(0);
      setPointPolicyNo(pointPolicyNo);
    } else {
      setPointPolicyNo(null);
    }
    // pointResponse.data.maxPoints.a;

  }, [pointResponse]);

  useEffect(() => {
    const _data = {
      campusCode: query.campus,
      classCode: query.classCode,
      learningYearCode: query.learningYearCode,
      semesterCode: query.semester,
      semesterDiv: query.semesterDiv,
    };

    execute(getPointInformation(_data));
  }, [location]);

  const onSave = async () => {
    if (isSaving) return;

    const saveMaxPoints = maxPoints.map(function (obj: any) {
      return { pointBranchCode: obj.pointBranchCode, maxPoint: obj.maxPoint };
    });

    if (pointSections.length === 0 || saveMaxPoints.length === 0) {
      enqueueSnackbar(t('program.point.message1'), warningVariant);
      return;
    }

    if (saveMaxPoints.length > 0) {
      for (let i = 0; i < saveMaxPoints.length; i++) {
        if (saveMaxPoints[i].pointBranchCode === undefined) {
          enqueueSnackbar(t('program.point.message2'), warningVariant);
          return;
        }
        if (
          saveMaxPoints[i].maxPoint === undefined || saveMaxPoints[i].maxPoint === 0) {
          enqueueSnackbar(t('program.point.message3'), warningVariant);
          return;
        }
      }
    }

    if (pointSections.length > 0) {
      for (let i = 0; i < pointSections.length; i++) {
        if (pointSections[i].sectionPoint === 0) {
          enqueueSnackbar(t('program.point.message4'), warningVariant);
          return;
        }
      }
    }

    const data = {
      pointPolicyNo,
      campusCode: query.campus,
      classCode: query.classCode,
      learningYearCode: query.learningYearCode,
      semesterCode: query.semester,
      semesterDiv: query.semesterDiv,
      maxPoints: saveMaxPoints,
      pointSections,
    };

    setIsSaving(true);
    try {
      await savePointInfomation(data);
      successNotification(t('common.message.save'));
      // 저장 후 재조회하여 pointPolicyNo 포함 최신 데이터 반영 (백엔드가 pointPolicyNo 미반환 시)
      const fetchData = {
        campusCode: query.campus,
        classCode: query.classCode,
        learningYearCode: query.learningYearCode,
        semesterCode: query.semester,
        semesterDiv: query.semesterDiv,
      };
      execute(getPointInformation(fetchData));
    } catch (err) {
      enqueueSnackbar(t('common.message.saveFail') ?? '저장에 실패했습니다.', warningVariant);
    } finally {
      setIsSaving(false);
    }
  };

  let [dummyOption, setDummyOption] = useState([
    {
      key: 'LA',
      value: 'LA',
      label: 'Language Arts',
      disabled: false,
    },
    {
      key: 'LG',
      value: 'LG',
      label: 'Language',
      disabled: false,
    },
    {
      key: 'TL',
      value: 'TL',
      label: 'Thinking LAB',
      disabled: false,
    },
    {
      key: 'AI',
      value: 'AI',
      label: 'AI Speaking',
      disabled: false,
    },
    {
      key: 'WC',
      value: 'WC',
      label: 'Writing Center',
      disabled: false,
    },
  ]);

  return (
    <div className='mt-5'>
      {(isLoading2 || isLoading3) && <Loader />}
      
      {classCode ? (
        <>
          <PointInput
            dummyOption={dummyOption}
            setDummyOption={setDummyOption}
            maxPoints={maxPoints}
            setMaxPoints={setMaxPoints}
          />
          <GemstonesInput
            pointSections={pointSections}
            setPointSections={setPointSections}
          />
        </>
      ) : (
        <TableEmptyBlock className='comm-nodata-area mt-3'>
          {t('class.class.text33')}
        </TableEmptyBlock>
      )}
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={onSave}
            text={t('common.button.save')}
            disabled={isSaving}
            // writeAuthYn={writeAuthYn}
          />
          <Button
            className="btn-basic gray"
            onClick={() => setClassCode('')}
            text={t('common.button.cancel')}
          />
        </div>
      </div>
    </div>
  );
};

export default PointTab;
